import './image-overlay.component.scss';

import { Configuration } from '@api/configuration';
import { createFromObservable, OverlayService } from '@codewyre/wyrekit-solid';
import Bin from '@icons/bin.svg';
import Close from '@icons/close.svg';
import Download from '@icons/download.svg';
import ChevronLeft from '@icons/left.svg';
import ChevronRight from '@icons/right.svg';
import { createSignal, Show } from 'solid-js';

import { SecurityService } from '../../../services/security.service';
import { UserService } from '../../../services/user.service';
import { useDependency } from '../../../shared';
import { ImageWrapper } from '../image-wrapper.component';
import { Image } from '../image.view-model';
import {
	ImageOverlayConfirmDeletionComponent,
	ImageOverlayConfirmDeletionComponentProps
} from './image-overlay.confirm-deletion';

export interface ImageOverlayProps {
	image: Image;
	imageList: Image[];
	showSize?: boolean;
	onRemove?: (image: Image) => void;
}

export function ImageOverlayComponent(props: ImageOverlayProps) {
	function getSibling(
		imageList: Image[], image: Image, step: number
	) {
		const index = imageList.indexOf(image);

		const nextIndexNegative = step < 0 && index <= 0;
		const nextIndexOutOfBounds = step > 0 && index >= imageList.length - 1;

		if (nextIndexNegative || nextIndexOutOfBounds) {
			return null;
		}

		return imageList[index + step];
	}

	const image = () => props.image;
	const showSize = () => props.showSize === undefined ?
		false :
		props.showSize;
	const imageList = () => props.imageList;
	const configuration = useDependency<Configuration>(Configuration);
	const securityService = useDependency<SecurityService>(SecurityService);

	const previous = () => !imageList().length ?
		null :
		getSibling(
			imageList(), props.image, -1
		);

	const next = () => !imageList().length ?
		null :
		getSibling(
			imageList(), props.image, 1
		);

	const overlayService = useDependency<OverlayService>(OverlayService);
	const userService = useDependency<UserService>(UserService);
	const [user] = createFromObservable(userService.user);
	const [
		controlsActive,
		setControlsActive
	] = createSignal(true);
	const [
		swipeOffset,
		setSwipeOffset
	] = createSignal(0);
	const [
		isSwiping,
		setIsSwiping
	] = createSignal(false);
	const [
		isDownloadingOriginal,
		setIsDownloadingOriginal
	] = createSignal(false);
	const [
		downloadProgress,
		setDownloadProgress
	] = createSignal(0);

	let touchStartX = 0;
	let touchStartY = 0;

	const handleTouchStart = (e: TouchEvent) => {
		touchStartX = e.touches[0].clientX;
		touchStartY = e.touches[0].clientY;
		setIsSwiping(true);
	};

	const handleTouchMove = (e: TouchEvent) => {
		if (!isSwiping()) {
			return;
		}

		const touchX = e.touches[0].clientX;
		const touchY = e.touches[0].clientY;
		const deltaX = touchX - touchStartX;
		const deltaY = touchY - touchStartY;

		// Only allow horizontal swipe if it's more horizontal than vertical
		if (Math.abs(deltaX) > Math.abs(deltaY)) {
			e.preventDefault();
			setSwipeOffset(deltaX);
		}
	};

	const handleTouchEnd = () => {
		const offset = swipeOffset();
		const threshold = 100;

		if (offset > threshold && previous()) {
			show(previous());
		} else if (offset < -threshold && next()) {
			show(next());
		}

		setSwipeOffset(0);
		setIsSwiping(false);
	};

	// FIX: sad
	function deleteImage(image: Image): void {
		overlayService.show<ImageOverlayConfirmDeletionComponentProps>({
			component: ImageOverlayConfirmDeletionComponent,
			props: {
				image,
				fallback: previous() ?? next() ?? null,
				images: [...imageList()],
				onRemove: props.onRemove
			}
		});
	}

	function show(nextImage: Image | null) {
		if (!nextImage) {
			return;
		}
		overlayService.close();
		overlayService.show({
			component: ImageOverlayComponent,
			props: {
				image: nextImage,
				imageList: imageList(),
				onRemove: props.onRemove,
				showSize: props.showSize
			}
		});
	}

	async function downloadOriginal() {
		if (!image().id || !image().createdBy?.id || isDownloadingOriginal()) {
			return;
		}

		setIsDownloadingOriginal(true);
		setDownloadProgress(0);

		try {
			const downloadUrl = `${configuration.basePath}/media/${image().createdBy.id}/download/${image().id}` +
				`?type=original&passcode=${securityService.getPasscode()}`;
			const blob = await new Promise<Blob>((resolve, reject) => {
				const xhr = new XMLHttpRequest();
				xhr.open('GET', downloadUrl, true);
				xhr.responseType = 'blob';

				xhr.onprogress = event => {
					if (!event.lengthComputable || event.total <= 0) {
						return;
					}

					setDownloadProgress(Math.min(100, Math.round(event.loaded / event.total * 100)));
				};

				xhr.onload = () => {
					if (xhr.status >= 200 && xhr.status < 300) {
						setDownloadProgress(100);
						resolve(xhr.response as Blob);

						return;
					}

					reject(new Error(`Failed to download image ${image().id}`));
				};

				xhr.onerror = () => reject(new Error(`Failed to download image ${image().id}`));
				xhr.onabort = () => reject(new Error(`Download aborted for image ${image().id}`));
				xhr.send();
			});
			const objectUrl = URL.createObjectURL(blob);
			const extension = image().file.type.replace(/^.*\//, '')
				.replace('jpeg', 'jpg');
			const filename = `image-${image().id}.${extension}`;

			const link = document.createElement('a');
			link.href = objectUrl;
			link.download = filename;
			link.rel = 'noopener noreferrer';
			document.body.append(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(objectUrl);
		} catch (error) {
			console.error('Download of original image failed', error);
		} finally {
			setIsDownloadingOriginal(false);
			setDownloadProgress(0);
		}
	}

	return <div
		class="image-overlay ai-center d-flex jc-center ff-column g-16 pt-64 pb-32"
		onClick={() => setControlsActive(!controlsActive())}
		onTouchStart={handleTouchStart}
		onTouchMove={handleTouchMove}
		onTouchEnd={handleTouchEnd}>

		<div
			class={'ta-center ' + (controlsActive() ?
				'' :
				'hide-image-overlays')}
			style={{
				transform: `translateX(${swipeOffset()}px)`,
				transition: isSwiping() ?
					'none' :
					'transform 0.3s ease-out'
			}}>
			<ImageWrapper
				image={image()}
				showTypeIcon={false}
				isPreview={false}
				onVideoClick={() => setControlsActive(!controlsActive())}
				images={imageList()} />
		</div>
		<div class="flex span"></div>
		<Show when={controlsActive()}>
			<Show when={image().createdBy}>
				<div class="author ta-center text-shadowed">
					<Show when={showSize()}>
						<div>
							<div class="weak">
								Größe
							</div>
							<div>{Math.round(image().size / 1024 / 1024)} MB</div>
						</div>
					</Show>
					<div>
						<div class="weak">
							Hochgeladen von
						</div>
						<div>{image().createdBy.name}</div>
					</div>
				</div>
			</Show>
			<div class="actions d-flex w-100 d-flex g-16 p-32 bs-bb">
				<div class="actions__left d-flex jc-start">
					<div>
						<Show when={previous()}>
							<button class="glass previous" onClick={e => {
								e.stopPropagation();
								show(previous());
							}}>
								<ChevronLeft />
							</button>
						</Show>
					</div>
				</div>
				<div class="actions__center flex d-flex ai-start jc-center g-16">
					<Show when={image().createdBy}>
						<div class="author ta-center text-shadowed">
							<Show when={showSize()}>
								<div>
									<div class="weak">
										Größe
									</div>
									<div>{Math.round(image().size / 1024 / 1024)} MB</div>
								</div>
							</Show>
							<div>
								<div class="weak">
									Hochgeladen von
								</div>
								<div>{image().createdBy.name}</div>
							</div>
						</div>
					</Show>

					<Show when={!image().id || isRegisteredImageByMe()}>
						<div class="ta-center">
							<button class="glass error delete" onClick={e => {
								e.stopPropagation();
								deleteImage(image());
							}}>
								<Bin />
							</button>
						</div>
					</Show>
					<Show when={image().id}>
						<div class="ta-center">
							<button
								class={`glass download${isDownloadingOriginal() ?
									' download--loading' :
									''}`}
								disabled={isDownloadingOriginal()}
								onClick={e => {
									e.stopPropagation();
									void downloadOriginal();
								}}>
								<Show when={isDownloadingOriginal()} fallback={<Download />}>
									<span class="download-progress-wrapper" aria-hidden="true">
										<span class="download-spinner"></span>
										<span class="download-progress">{downloadProgress()}%</span>
									</span>
								</Show>
							</button>
						</div>
					</Show>
					<div class="ta-center">
						<button class="close glass" onClick={e => {
							e.stopPropagation();
							overlayService.close();
						}}>
							<Close />
						</button>
					</div>
				</div>
				<div class="actions__right d-flex jc-end">
					<div>
						<Show when={next()}>
							<button class="next glass" onClick={e => {
								e.stopPropagation();
								show(next());
							}}>
								<ChevronRight />
							</button>
						</Show>
					</div>
				</div>
			</div>
		</Show>
	</div>;

	function isRegisteredImageByMe(): boolean {
		return !!(image().id && user() && user()?.id === image().createdBy.id);
	}
}
